import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Card from '../../../components/ui/Card';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { updateUser } from '../../../api/users';
import { pushToast } from '../../../components/ui/toastBus';
import { usePermission } from '../../../auth/usePermission';

export default function ProfileTab({ user }) {
  const { can } = usePermission();
  const queryClient = useQueryClient();
  const [form, setForm] = useState({ name: user.name, email: user.email, phone: user.phone || '', password: '' });
  const [errors, setErrors] = useState({});

  const mutation = useMutation({
    mutationFn: (payload) => updateUser(user.id, payload),
    onSuccess: () => {
      pushToast({ type: 'success', message: 'User updated.' });
      setErrors({});
      setForm((f) => ({ ...f, password: '' }));
      queryClient.invalidateQueries({ queryKey: ['users', String(user.id)] });
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (err) => setErrors(err.response?.data?.errors || {}),
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = { name: form.name, email: form.email, phone: form.phone };
    if (form.password) payload.password = form.password;
    mutation.mutate(payload);
  };

  const fieldError = (field) => errors[field]?.[0];
  const readOnly = !can('users.update');

  return (
    <Card className="max-w-lg">
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Name"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          error={fieldError('name')}
          disabled={readOnly}
        />
        <Input
          label="Email"
          type="email"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          error={fieldError('email')}
          disabled={readOnly}
        />
        <Input
          label="Phone"
          value={form.phone}
          onChange={(e) => setForm({ ...form, phone: e.target.value })}
          error={fieldError('phone')}
          disabled={readOnly}
        />
        <Input
          label="Reset password"
          type="password"
          placeholder="Leave blank to keep current password"
          value={form.password}
          onChange={(e) => setForm({ ...form, password: e.target.value })}
          error={fieldError('password')}
          disabled={readOnly}
        />

        {!readOnly && (
          <Button type="submit" loading={mutation.isPending}>
            Save changes
          </Button>
        )}
      </form>
    </Card>
  );
}
